import * as React from 'react';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { ErrorPage } from 'containers';
require('./style.scss');

type SimpleError = string | { _error: string };
type Errors = SimpleError | Errors[] | { [key: string]: Errors };

type FormattedError = {
	error: string;
	status?: number;
	statusText?: string;
};

interface IProps {
	message?: any;
	error?: boolean;
	formErrors?: Errors;
	submitFailed?: boolean;
	showFormErrors?: boolean;
	mapErrorKeyToMessage?: (key: string, t: (key: string) => string, index?: number) => string;
	t?: (key: string) => string;
	customFormErrorsParseFunc?: () => FormattedError[];
}

type DefaultProps = Readonly<typeof defaultProps>;

const defaultProps = {
	showFormErrors: true,
};

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class ErrorSummary extends React.Component<IProps, {}> {
	public static defaultProps: DefaultProps;

	private fillErrorsRecursive(keys: Array<string | number>, errors: Errors): FormattedError[] {
		const { mapErrorKeyToMessage, t } = this.props;
		const stringKeys = keys.filter((k) => typeof k === 'string') as string[];
		const lastKey = stringKeys.length ? stringKeys[stringKeys.length - 1] : null;
		const indexes = keys.filter((k) => typeof k === 'number') as number[];
		const lastIndex = indexes.length ? indexes[indexes.length - 1] : null;
		if (typeof errors === 'string') {
			return [{ error: mapErrorKeyToMessage(lastKey || '', t, lastIndex) + ': ' + errors }];
		}
		if (typeof errors !== 'object' || !errors) {
			return [];
		}
		if (Array.isArray(errors)) {
			return errors.reduce(
				(acc, val, i) => [...acc, ...this.fillErrorsRecursive([...keys, i], val)],
				[] as FormattedError[],
			);
		}
		if ('_error' in errors) {
			return [{ error: mapErrorKeyToMessage(lastKey || '', t, lastIndex) + ': ' + errors._error }];
		}
		return Object.keys(errors).reduce(
			(acc, valKey) => [...acc, ...this.fillErrorsRecursive([...keys, valKey], errors[valKey])],
			[] as FormattedError[],
		);
	}

	public render() {
		const { error, message, formErrors, showFormErrors, customFormErrorsParseFunc } = this.props;

		let errors: FormattedError[] = [];

		// show form errors only on submit (failed)
		if (showFormErrors && formErrors && this.props.submitFailed) {
			if (customFormErrorsParseFunc) {
				errors = customFormErrorsParseFunc();
			} else {
				errors = this.fillErrorsRecursive([], formErrors);
			}
		}

		// show validation error(422) or corresponding error page
		if (error) {
			if (typeof message == 'string') {
				return <ErrorPage {...this.props} message={{ error: message }} isSummary={true} />;
			}
			if (message.status === 422) {
				message?.error?.forEach((errorMessage: string) => {
					if (errorMessage.length > 0) {
						errors.push({
							status: message.status,
							statusText: message.statusText,
							error: errorMessage,
						});
					}
				});
			} else {
				return <ErrorPage {...this.props} message={message} isSummary={true} />;
			}
		}

		if (errors.length < 1) {
			return null;
		}

		return (
			<div className="errorSummary">
				<ul>
					{errors.map((error, i) => (
						<li key={'error_summary_' + i}>{error.error}</li>
					))}
				</ul>
			</div>
		);
	}
}

ErrorSummary.defaultProps = defaultProps;

export { ErrorSummary };
